import { useAuth } from '@/auth/auth-provider'
import { isSuper, parsePrincipalFromUser } from '@/auth/principal'
import { ConfigFormHeader, ConfigSectionContext, ConfigViewOnlyContext } from '@/components/ConfigFormHeader'
import { useDealership } from '@/contexts/DealershipContext'
import { ConfigurationType, getConfiguration, saveConfiguration } from '@/services/configuration.api'
import { CONFIG_SECTIONS, RemindersFollowUpSectionData } from '@/types/configuration'
import { Card, Group, LoadingOverlay, NavLink, Paper, ScrollArea, Stack, Text, Title } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { BusinessBasicsForm } from './forms/BusinessBasicsForm'
import { ComplianceRiskForm } from './forms/ComplianceRiskForm'
import { ConversationStyleForm } from './forms/ConversationStyleForm'
import { DiscoveryForm } from './forms/DiscoveryForm'
import { EngagementChannelsForm } from './forms/EngagementChannelsForm'
import { HoursCoverageForm } from './forms/HoursCoverageForm'
import { PrequalificationForm } from './forms/PrequalificationForm'
import { RemindersFollowUpForm } from './forms/RemindersFollowUpForm'
import { StoreContextForm } from './forms/StoreContextForm'

/** Load one configuration section; null when it could not be loaded. */
async function fetchSectionData(dealershipId: string, section: ConfigurationType): Promise<any> {
  if (section === 'reminders_follow_up') {
    const [recoveryRes, unsoldRes, remindersRes] = await Promise.all([
      getConfiguration(dealershipId, 'appointment_recovery'),
      getConfiguration(dealershipId, 'showroom_unsold_recovery'),
      getConfiguration(dealershipId, 'appointment_reminders'),
    ])
    const data: RemindersFollowUpSectionData = {
      appointmentRecovery: recoveryRes.success && recoveryRes.data !== undefined ? recoveryRes.data : null,
      showroomUnsoldRecovery: unsoldRes.success && unsoldRes.data !== undefined ? unsoldRes.data : null,
      appointmentReminders: remindersRes.success && remindersRes.data !== undefined ? remindersRes.data : null,
    }
    return data
  }
  const response = await getConfiguration(dealershipId, section)
  return response.success && response.data ? response.data : null
}

export function DealershipConfiguration() {
  const { user } = useAuth()
  const principal = useMemo(() => parsePrincipalFromUser(user), [user])
  const { selectedEntity } = useDealership()
  const [selectedSection, setSelectedSection] = useState<ConfigurationType>('business_basics')
  const [configData, setConfigData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  // Staff see configuration but cannot change it; the API refuses their writes as well.
  const isViewOnly = !isSuper(principal)

  useEffect(() => {
    if (!selectedEntity || selectedEntity.type !== 'dealership') {
      return
    }

    const dealershipId = selectedEntity.id

    const loadConfiguration = async (section: ConfigurationType) => {
      setIsLoading(true)
      try {
        setConfigData(await fetchSectionData(dealershipId, section))
      } catch (error) {
        console.error('Failed to load configuration:', error)
        setConfigData(null)
      } finally {
        setIsLoading(false)
      }
    }

    // Reset config data when section changes to show loading state immediately
    setConfigData(null)
    setIsLoading(true)
    loadConfiguration(selectedSection)
  }, [selectedSection, selectedEntity])

  // Another user may have saved since this page loaded. When the tab is shown again, reload the section, and
  // only replace what is on screen if the saved configuration changed, so unsaved edits survive a tab switch.
  useEffect(() => {
    if (!selectedEntity || selectedEntity.type !== 'dealership') {
      return
    }
    const dealershipId = selectedEntity.id

    const refreshIfChanged = async () => {
      if (document.visibilityState !== 'visible' || isSaving) return
      try {
        const fresh = await fetchSectionData(dealershipId, selectedSection)
        if (fresh === null) return
        setConfigData((current: any) => (JSON.stringify(current) === JSON.stringify(fresh) ? current : fresh))
      } catch {
        // Keep what is on screen; the next tab switch tries again.
      }
    }

    document.addEventListener('visibilitychange', refreshIfChanged)
    return () => document.removeEventListener('visibilitychange', refreshIfChanged)
  }, [selectedSection, selectedEntity, isSaving])

  if (!selectedEntity || selectedEntity.type !== 'dealership') {
    return <Text>Please select a dealership</Text>
  }

  const dealershipId = selectedEntity.id

  // Handle section selection
  const handleSectionSelect = (section: ConfigurationType) => {
    setSelectedSection(section)
  }

  // Handle save
  const handleSave = async (data: any) => {
    if (isViewOnly) {
      return
    }
    setIsSaving(true)
    try {
      const response = await saveConfiguration(dealershipId, selectedSection, data)
      if (response.success) {
        notifications.show({
          title: 'Success',
          message: 'Configuration saved successfully',
          color: 'green',
          icon: <IconCheck size={16} />,
        })
        setConfigData(data)
      } else {
        notifications.show({
          title: 'Error',
          message: response.error || 'Failed to save configuration',
          color: 'red',
          icon: <IconX size={16} />,
        })
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'An unexpected error occurred',
        color: 'red',
        icon: <IconX size={16} />,
      })
    } finally {
      setIsSaving(false)
    }
  }

  // Get the currently selected section info
  const currentSection = CONFIG_SECTIONS.find(s => s.id === selectedSection)

  // Render form based on selected section
  const renderForm = () => {
    if (isLoading) {
      return (
        <Stack gap="md">
          <ConfigFormHeader onReset={() => undefined} isSaving={false} disabled />
          <Card shadow="sm" padding="xl" radius="md" withBorder style={{ position: 'relative', minHeight: 400 }}>
            <LoadingOverlay visible={true} />
          </Card>
        </Stack>
      )
    }

    const formProps = {
      data: configData,
      onSave: handleSave,
      isSaving,
    }

    switch (selectedSection) {
      case 'business_basics':
        return <BusinessBasicsForm {...formProps} />
      case 'hours_coverage':
        return <HoursCoverageForm {...formProps} />
      case 'store_context':
        return <StoreContextForm {...formProps} />
      case 'engagement_channels':
        return <EngagementChannelsForm {...formProps} />
      case 'conversation_style':
        return <ConversationStyleForm {...formProps} />
      case 'conversation_flow':
        return <DiscoveryForm {...formProps} />
      case 'reminders_follow_up':
        return <RemindersFollowUpForm data={configData} />
      case 'compliance_risk':
        return <ComplianceRiskForm {...formProps} />
      case 'prequalification':
        return <PrequalificationForm {...formProps} />
      default:
        return <Text>Unknown configuration section</Text>
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Group gap="xs" align="baseline">
            <Title order={2}>Dealership Configuration:</Title>
            <Text size="xl" fw={700} c="blue">
              {dealershipId}
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            Configure AI behavior, integrations, and business settings for {selectedEntity.name}
          </Text>
        </div>
      </Group>

      <Group align="flex-start" gap="md">
        {/* Configuration Navigation Sidebar */}
        <Paper shadow="sm" p="md" radius="md" withBorder style={{ width: 280 }}>
          <ScrollArea h={600}>
            <Stack gap="xs">
              <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb="xs">
                Configuration Sections
              </Text>
              {CONFIG_SECTIONS.map(section => {
                const IconComponent = section.icon
                return (
                  <NavLink
                    key={section.id}
                    active={selectedSection === section.id}
                    label={
                      <Stack gap={0}>
                        <Text size="sm" fw={500}>
                          {section.title}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {section.description}
                        </Text>
                      </Stack>
                    }
                    leftSection={<IconComponent size={20} stroke={1.5} />}
                    onClick={() => handleSectionSelect(section.id as ConfigurationType)}
                  />
                )
              })}
            </Stack>
          </ScrollArea>
        </Paper>

        {/* Configuration Form */}
        <div style={{ flex: 1 }}>
          {/* Each form starts with ConfigFormHeader, which titles the section from this context. The
              fieldset disables every input for staff; the header's buttons read the view-only flag. */}
          <ConfigSectionContext.Provider value={currentSection ?? null}>
            <ConfigViewOnlyContext.Provider value={isViewOnly}>
              <fieldset disabled={isViewOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                {renderForm()}
              </fieldset>
            </ConfigViewOnlyContext.Provider>
          </ConfigSectionContext.Provider>
        </div>
      </Group>
    </Stack>
  )
}
